import type { ParticipantQosData, QosMetrics } from './overview-types';

interface Props {
  data: ParticipantQosData;
}

export function QosDetailPanel({ data }: Props) {
  if (data.participants.length === 0) {
    return <p className="text-xs text-muted-foreground py-2">No participant QoS data available</p>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-left uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2">Participant</th>
            <th className="px-3 py-2">Audio</th>
            <th className="px-3 py-2">Video</th>
            <th className="px-3 py-2">Screen Share</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {data.participants.map((p, i) => (
            <tr key={i} className="hover:bg-secondary/30">
              <td className="px-3 py-2">
                <div className="text-foreground font-medium">{p.name}</div>
                {p.device && <div className="text-muted-foreground text-[10px]">{p.device}</div>}
              </td>
              <td className="px-3 py-2"><QosCell metrics={p.audio} /></td>
              <td className="px-3 py-2"><QosCell metrics={p.video} /></td>
              <td className="px-3 py-2">
                {p.screenShare ? <QosCell metrics={p.screenShare} /> : <span className="text-muted-foreground">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QosCell({ metrics }: { metrics: QosMetrics }) {
  const hasData = metrics.bitrate || metrics.latency || metrics.jitter || metrics.packetLoss;
  if (!hasData) return <span className="text-muted-foreground">—</span>;

  return (
    <div className="space-y-0.5">
      {metrics.latency && (
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground w-12">Latency</span>
          <QosBar value={metrics.latency} unit="ms" thresholds={[100, 200, 400]} />
        </div>
      )}
      {metrics.jitter && (
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground w-12">Jitter</span>
          <QosBar value={metrics.jitter} unit="ms" thresholds={[20, 50, 100]} />
        </div>
      )}
      {metrics.packetLoss && (
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground w-12">Loss</span>
          <QosBar value={metrics.packetLoss} unit="%" thresholds={[1, 3, 5]} />
        </div>
      )}
      {metrics.resolution && (
        <div className="text-muted-foreground">{metrics.resolution}</div>
      )}
    </div>
  );
}

function QosBar({ value, unit, thresholds }: { value: string; unit: string; thresholds: [number, number, number] }) {
  const num = parseFloat(value);
  const display = isNaN(num) ? value : `${num}${unit}`;

  let color = 'bg-green-500';
  if (!isNaN(num)) {
    if (num >= thresholds[2]) color = 'bg-red-500';
    else if (num >= thresholds[1]) color = 'bg-orange-500';
    else if (num >= thresholds[0]) color = 'bg-yellow-500';
  }

  const maxVal = thresholds[2] * 1.5;
  const width = isNaN(num) ? 50 : Math.min((num / maxVal) * 100, 100);

  return (
    <div className="flex items-center gap-1.5 flex-1">
      <div className="h-1.5 flex-1 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${width}%` }} />
      </div>
      <span className="text-foreground text-[10px] w-12 text-right">{display}</span>
    </div>
  );
}
